import { useRef, useEffect, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import styles from './TrustBar.module.css';

function useCountUp(target, duration = 1800, enabled = false, isStatic = false) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!enabled || isStatic) return;
    let startTime = null;
    let raf;

    const tick = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      setCount(Math.floor(eased * target));
      if (progress < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        setCount(target);
      }
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, enabled, isStatic]);

  return count;
}

function Stat({ display, number, suffix, label, isStatic, delay }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.5 });
  const count = useCountUp(number, 1800, isInView, isStatic);

  const shown = isStatic ? display : count.toLocaleString();

  return (
    <motion.div
      ref={ref}
      className={styles.stat}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.5 }}
      transition={{ duration: 0.6, delay }}
    >
      <div className={styles.statNumber}>
        {shown}
        {suffix && <span className={styles.statSuffix}>{suffix}</span>}
      </div>
      <div className={styles.statLabel}>{label}</div>
    </motion.div>
  );
}

const STATS = [
  { display: '4.8', suffix: '★', label: 'Google & Justdial Rating', isStatic: true },
  { number: 1373, suffix: '+', label: 'Happy Clients Reviewed', isStatic: false },
  { number: 31, suffix: 'K+', label: 'Instagram Followers', isStatic: false },
  { number: 25, suffix: '+', label: 'Years of Excellence', isStatic: false },
];

export default function TrustBar() {
  return (
    <section className={styles.trustBar} aria-label="Shobha Salon at a glance">
      <div className={styles.inner}>
        {STATS.map((stat, i) => (
          <Stat key={stat.label} {...stat} delay={i * 0.1} />
        ))}
      </div>
    </section>
  );
}
